import { useCallback, useMemo, useState } from 'react';
import { Badge, Text, Button, Group, Stack, Box } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { ColumnDef, Row } from '@tanstack/react-table';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { useLeadRepository } from '@ayocore/erp/crm/client/rpc';
import type { Lead } from '@ayocore/erp/crm/schemas/lead.schema';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import { FilterByStatus } from './filters/FilterByStatus';
import { FilterByManager } from './filters/FilterByManager';
import { FilterByCreatedDate } from './filters/FilterByCreatedDate';

type LeadExtended = Lead & {
  lead_status?: { id: number; name?: string; color?: string | null } | null;
  lead_type?: { id: number; name?: string; slug?: string } | null;
  manager?: { id: number; name?: string; email?: string } | null;
};

export interface LeadPickerModalProps extends Record<string, unknown> {
  /**
   * Режим выбора: 'single' — одна заявка, 'multiple' — несколько.
   * При двойном клике в режиме 'single' сразу подтверждает выбор.
   * По умолчанию: 'single'.
   */
  selectionMode?: 'single' | 'multiple';
  /**
   * Показывать только заявки без сделки.
   *
   * Для привязки из карточки сделки — обязательно: заявка несёт ровно одну
   * сделку (`crm_leads.deal_id`), и уже привязанную выбирать нельзя. Прятать
   * её честнее, чем показать и отказать после выбора.
   */
  onlyWithoutDeal?: boolean;
  /**
   * Вызывается при подтверждении выбора.
   * Всегда возвращает массив Lead[]: при single — массив из одного элемента.
   */
  onLeadSelected: (leads: Lead[]) => void;
  /** Вызывается при отмене (кнопка «Отмена»). Обычно закрывает модалку. */
  onClose?: () => void;
}

/**
 * LeadPickerModal — модальное окно выбора заявок.
 *
 * Близнец DealPickerModal, только над заявками: таблица со своими фильтрами
 * и поиском, клик выделяет строку, двойной в режиме 'single' сразу
 * подтверждает выбор.
 *
 * Компонент используется через useModal и не содержит обёртки `<Modal>` —
 * её даёт хук. Панель с кнопками закреплена снизу, чтобы «Выбрать» не
 * уезжала под край при встраивании в чужую модалку.
 *
 * @example
 * ```tsx
 * const leadPicker = useModal(LeadPickerModal);
 *
 * leadPicker.open({
 *   selectionMode: 'single',
 *   onlyWithoutDeal: true,
 *   onLeadSelected: leads => { ... ; leadPicker.close(); },
 *   onClose: () => leadPicker.close(),
 * }, { size: 'xl', title: 'Выбрать заявку' });
 * ```
 */
const LeadPickerModal = ({
  selectionMode = 'single',
  onlyWithoutDeal = false,
  onLeadSelected,
  onClose,
}: LeadPickerModalProps) => {
  const [selectedLeads, setSelectedLeads] = useState<LeadExtended[]>([]);

  const baseRepository = useLeadRepository().withRelations().limit(100);
  const repository = useMemo(
    () => (onlyWithoutDeal ? baseRepository.filterByHasDeal(false) : baseRepository),
    // baseRepository — новая ссылка на каждый рендер (прокси иммутабелен),
    // поэтому в зависимостях только флаг: иначе таблица перезапрашивалась бы.
    [onlyWithoutDeal]
  );

  const columns = useMemo(
    (): ColumnDef<LeadExtended>[] => [
      {
        id: 'id',
        header: '№',
        accessorKey: 'id',
        enableSorting: true,
        size: 80,
        cell: ({ row }) => <Text size='xs'>{row.original.id}</Text>,
      },
      {
        id: 'created_at',
        header: 'Дата',
        accessorKey: 'created_at',
        enableSorting: true,
        size: 110,
        cell: ({ row }) => {
          const value = row.original.created_at;
          if (!value) return <Text size='xs'>—</Text>;
          const date = new Date(value);
          return (
            <Text size='xs'>
              {Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU')}
            </Text>
          );
        },
      },
      {
        id: 'title',
        header: 'Название',
        accessorKey: 'title',
        enableSorting: true,
        size: 280,
        cell: ({ row }) => <Text size='xs'>{row.original.title}</Text>,
      },
      {
        id: 'lead_status.name',
        header: 'Статус',
        accessorFn: row => row.lead_status?.name ?? '',
        enableSorting: true,
        size: 150,
        cell: ({ row }) => {
          const status = row.original.lead_status;
          if (!status) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return (
            <Badge
              color={status.color || 'gray'}
              variant='light'
              size='sm'
              style={{ textTransform: 'none', fontWeight: 'normal' }}
            >
              {status.name}
            </Badge>
          );
        },
      },
      {
        id: 'manager.name',
        header: 'Менеджер',
        accessorFn: row => row.manager?.name ?? '',
        enableSorting: true,
        size: 180,
        cell: ({ row }) => {
          const manager = row.original.manager;
          if (!manager) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return <Text size='xs'>{manager.name || manager.email || `ID: ${manager.id}`}</Text>;
        },
      },
      {
        id: 'amount',
        header: 'Сумма',
        accessorKey: 'amount',
        enableSorting: true,
        size: 130,
        cell: ({ row }) => {
          const amount = row.original.amount;
          if (amount == null) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return (
            <Text size='xs' style={{ textAlign: 'right' }}>
              {new Intl.NumberFormat('ru-RU').format(Number(amount))} ₽
            </Text>
          );
        },
      },
    ],
    []
  );

  /** Клик по строке: в режиме single — заменяет выбор, в multiple — переключает */
  const handleRowClick = useCallback(
    (_row: Row<LeadExtended>, rowData: LeadExtended) => {
      if (selectionMode === 'single') {
        setSelectedLeads([rowData]);
      } else {
        setSelectedLeads(prev =>
          prev.some(l => l.id === rowData.id)
            ? prev.filter(l => l.id !== rowData.id)
            : [...prev, rowData]
        );
      }
    },
    [selectionMode]
  );

  /** Двойной клик в режиме single — сразу подтверждает выбор */
  const handleRowDoubleClick = useCallback(
    (_row: Row<LeadExtended>, rowData: LeadExtended) => {
      if (selectionMode === 'single') {
        onLeadSelected([rowData]);
      }
    },
    [selectionMode, onLeadSelected]
  );

  /** Подсвечиваем выбранные строки */
  const getRowProps = useCallback(
    (row: Row<LeadExtended>): React.HTMLAttributes<HTMLDivElement> => {
      const isSelected = selectedLeads.some(l => l.id === row.original.id);
      return {
        style: {
          backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
          cursor: 'pointer',
        },
      };
    },
    [selectedLeads]
  );

  const selectionHint =
    selectedLeads.length > 0
      ? `Выбрано: ${selectedLeads.length}`
      : selectionMode === 'single'
        ? 'Нажмите на строку или дважды щёлкните для быстрого выбора'
        : 'Выберите одну или несколько заявок';

  return (
    <Stack gap='md'>
      <RepositoryTable<LeadExtended>
        id='crm-leads-picker'
        repository={repository as unknown as RpcRepositoryClient<LeadExtended>}
        columns={columns}
        filters={[FilterByStatus, FilterByManager, FilterByCreatedDate]}
        onRowClick={handleRowClick}
        onRowDoubleClick={selectionMode === 'single' ? handleRowDoubleClick : undefined}
        getRowProps={getRowProps}
        tableBodyHeight={320}
        rowHeight={1}
        minSearchLength={3}
        searchPlaceholder='Поиск от 3 символов'
      />

      {/* Футер закреплён снизу — см. тот же приём в DealPickerModal: при
          встраивании пикера в чужую модалку кнопка «Выбрать» иначе уезжает
          под нижний край. */}
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--mantine-color-gray-3)',
          paddingTop: 'var(--mantine-spacing-xs)',
        }}
      >
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            {selectionHint}
          </Text>
          <Group>
            <Button variant='default' onClick={onClose}>
              Отмена
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              onClick={() => onLeadSelected(selectedLeads)}
              disabled={selectedLeads.length === 0}
            >
              Выбрать
            </Button>
          </Group>
        </Group>
      </Box>
    </Stack>
  );
};

export default LeadPickerModal;
